import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// api
import ApiKey from '../api/ApiKey';
import BaseApi from '../api/BaseApi';
// components
import Footer from '../element/Footer';
import NavBar from '../element/NavBar';
import SmallLoading from '../element/SmallLoading';
import CVDescription from '../element/CVDescription';
// helpers
import { logOutIfTokenExpired, resultInfo } from '../helpers';
// hooks
import { useWindowSize } from '../../Hooks/use-window-size';
// mui
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

const JSMyCV = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const { t } = useTranslation('global');
  const userType = Cookies.get('user_type');
  const tokenKey = Cookies.get('tokenClient');

  const [loading, setLoading] = useState(true);
  const [cvDetails, setCvDetails] = useState({});
  const [smallLoading, setSmallLoading] = useState(false);

  const getData = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        BaseApi + '/candidates/getCv',
        { id },
        {
          headers: {
            key: ApiKey,
            token: tokenKey,
            contentType: 'application/json',
          },
        },
      );

      setLoading(false);

      if (response.data.status === 200) {
        setCvDetails(response.data.response);
      } else if (response.data.status === 400) {
        navigate('/');

        Cookies.remove('fname');
        Cookies.remove('user_id');
        Cookies.remove('user_type');
        Cookies.remove('tokenClient');

        resultInfo('warning', response.data.message, t('jobseekerManageAlert.close'));
      } else {
        resultInfo('error', response.data.message, t('jobseekerManageAlert.close'));
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else if (error.response.status === 403) {
        Swal.fire({
          icon: 'warning',
          title: error.response.data.message,
          confirmButtonText: t('jobseekerManageAlert.close'),
        }).then(result => {
          if (result.isConfirmed) {
            navigate('/');
          }
        });
      }

      console.error('Cannot get profile data', error);
    }
  };

  const handleDownloadResume = async () => {
    try {
      setSmallLoading(true);

      const response = await axios.post(
        BaseApi + '/download-user-pdf',
        {
          id,
          contacts: true,
        },
        {
          responseType: 'blob',
          headers: {
            key: ApiKey,
            token: tokenKey,
          },
        },
      );

      setSmallLoading(false);

      const url = window.URL.createObjectURL(response.data);
      const filename = response.headers['content-disposition'] ? response.headers['content-disposition'].split('filename=')[1] : 'resume-grc.pdf';

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename.replace(/['"]/g, ''));
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      setSmallLoading(false);

      if (error.response.status === 400) {
        resultInfo('warning', t('jobseekerManageAlert.contactsError'), t('jobseekerManageAlert.close'));
      } else {
        resultInfo('error', t('jobDescription.unsubscribeError'), t('jobseekerManageAlert.close'));
      }

      console.error('Cannot download resume', error);
    }
  };

  useEffect(() => {
    if (userType === 'recruiter' || !tokenKey) {
      navigate('/user/jobseekerlogin');
    } else {
      getData();
      window.scrollTo(0, 0);
    }
  }, [tokenKey, navigate, userType]); // eslint-disable-line

  return (
    <>
      <NavBar />
      {
        loading
          ? (
            <div className='loader-container'></div>
          ) : (
            <>
              <div className='container my-5 JSMyCV'>
                <div className='row'>
                  <div className='col cheaps'>
                    <button className='btn btn-primary button2' onClick={() => window.history.back()}>
                      <ChevronLeftOutlinedIcon />
                      {' '}
                      {width >= 768 && t('cvDescription.back')}
                    </button>
                    <button className='btn btn-primary button1' onClick={() => navigate(`/candidates/editCV/${id}`)}>
                      <EditOutlinedIcon />
                      {' '}
                      {width >= 768 && t('cvDescription.edit')}
                    </button>
                    <button disabled className='btn btn-primary button1'>
                      <FileCopyOutlinedIcon />
                      {' '}
                      {width >= 768 && t('cvDescription.copy')}
                    </button>
                    <button
                      type='button'
                      disabled={smallLoading}
                      className='btn btn-primary button1'
                      onClick={() => handleDownloadResume()}
                    >
                      <FileDownloadOutlinedIcon />
                      {' '}
                      {width >= 768 && t('cvDescription.downloadPDF')}
                      {' '}
                      {smallLoading && <SmallLoading width='24px' height='24px' color='#FFFFFF' />}
                    </button>
                    <button disabled className='btn btn-primary button2'>
                      <DeleteIcon />
                      {' '}
                      {width >= 768 && t('cvDescription.delete')}
                    </button>
                  </div>
                </div>
                <CVDescription cvDetails={cvDetails} />
              </div>
              <Footer />
            </>
          )
      }
    </>
  );
};

export default JSMyCV;
